import type { OfficePresetManifest } from "@bezoned/shared/schemas/office/office-preset-manifest.schema.js";
import { useEffect, useMemo, useState } from "react";
import type { Texture } from "three";
import { DoubleSide, Euler, TextureLoader, Vector3 } from "three";

import { recordErrorInSentry } from "../../utils/init-sentry.js";

interface TextureImage {
  width: number;
  height: number;
}

interface OfficeLogoProps {
  config: OfficePresetManifest["logo"];
  customLogoUrl: string | null;
}

const DEFAULT_LOGO_URL = "/images/bezoned-logo.png";

export const OfficeLogo = ({ customLogoUrl, config }: OfficeLogoProps) => {
  const [texture, setTexture] = useState<Texture | null>(null);
  const { maxSize, position, rotation } = config;

  const logoUrl = customLogoUrl ?? DEFAULT_LOGO_URL;

  //? This code is responsible for loading the logo texture from URL and reporting errors to Sentry if it fails.
  //? The goal was to keep the office running even if the logo is not available.
  useEffect(() => {
    let isActive = true;
    let activeTexture: Texture | null = null;

    const cleanup = () => {
      isActive = false;

      if (activeTexture) {
        activeTexture.dispose();
      }
    };

    setTexture(null);

    if (!logoUrl) {
      return cleanup;
    }

    const loader = new TextureLoader();

    loader.load(
      logoUrl,
      (loadedTexture) => {
        if (!isActive) {
          loadedTexture.dispose();
          return;
        }

        activeTexture = loadedTexture;
        setTexture(loadedTexture);
      },
      undefined,
      (error) => {
        if (!isActive) {
          return;
        }

        const sentryError =
          error instanceof Error
            ? error
            : new Error("Failed to load office logo texture.");
        recordErrorInSentry(sentryError, { extra: { logoUrl } });
      }
    );

    return cleanup;
  }, [logoUrl]);

  const size = useMemo(() => {
    const image = texture?.image as TextureImage | undefined;

    if (!(image?.width && image?.height)) {
      return null;
    }

    const imageAspectRatio = image.width / image.height;
    const boundsAspectRatio = maxSize.width / maxSize.height;

    let width: number;
    let height: number;

    if (imageAspectRatio > boundsAspectRatio) {
      // Image is wider than bounds - constrain by width
      width = maxSize.width;
      height = maxSize.width / imageAspectRatio;
    } else {
      // Image is taller than bounds - constrain by height
      height = maxSize.height;
      width = maxSize.height * imageAspectRatio;
    }

    return { height, width };
  }, [texture, maxSize]);

  if (!size) {
    return null;
  }

  return (
    <mesh
      position={new Vector3(position[0], position[1], position[2])}
      rotation={new Euler(rotation[0], rotation[1], rotation[2])}
    >
      <planeGeometry args={[size.width, size.height]} />
      <meshBasicMaterial map={texture} side={DoubleSide} transparent />
    </mesh>
  );
};
